import { configureStore } from "@reduxjs/toolkit";

import { ServerConfig, WidgetEnv } from "..";
import { log } from "../utils";

import { BrowserStateStorage } from "./BrowserStateStorage";
import { createDefaultState } from "./DefaultState";
import { LocalStorage } from "./LocalStorage";
import persistStateReducer from "./reducers/persistStateReducer";
import { storeHandler } from "./reducers/rootReducer";


const ADMIN_STORE_KEY = "AdminAccessTimes";
const ADMIN_STORE_MAX = 20;

interface AdminAccessTimes {
  [sessionId: string]: {
    lastAccessMs: number;
    dataStorageKey: string;
  };
}

export function createChatStore(config: WidgetEnv, dataStorage?: Storage) {
  if (!dataStorage) {
    if (typeof window !== "undefined") {
      dataStorage = localStorage;
    } else {
      dataStorage = new LocalStorage();
    }
  }

  const connection = config.connection;
  const joinSessionId = new URL(window.location.href).searchParams?.get("sessionId");

  let dataStorageKey: string;
  const now = new Date().getTime();

  if (joinSessionId) {
    const adminTimesData: string = dataStorage.getItem(ADMIN_STORE_KEY);
    const adminTimes: AdminAccessTimes = adminTimesData ? JSON.parse(adminTimesData) : {};

    if (adminTimes[joinSessionId]) {
      log(`ChatStore: reusing session: ${joinSessionId}`);
      adminTimes[joinSessionId].lastAccessMs = now;
      dataStorageKey = adminTimes[joinSessionId].dataStorageKey;
    } else {
      if (Object.keys(adminTimes).length >= ADMIN_STORE_MAX) {
        const evictedSessionId = evictAdminSession(adminTimes);
        log(`ChatStore: evicting session: ${evictedSessionId}`);
        dataStorage.removeItem(adminTimes[evictedSessionId].dataStorageKey);
        adminTimes[evictedSessionId] = undefined;
      }

      dataStorageKey = generateKey(connection, joinSessionId);
      adminTimes[joinSessionId] = {
        lastAccessMs: now,
        dataStorageKey,
      };
    }

    dataStorage.setItem(ADMIN_STORE_KEY, JSON.stringify(adminTimes));
  } else {
    dataStorageKey = generateKey(connection);
  }

  const storage = new BrowserStateStorage(dataStorage, dataStorageKey);

  const defaultState = createDefaultState({
    accessToken: config.accessToken,
    userId: config.userId,
    attributes: config.attributes,
    sessionExpiration: config.sessionExpiration
  }, {
    enableErrorOverlay: config.enableErrorOverlay
  });

  const chatReducer = persistStateReducer(storage, defaultState, storeHandler);

  // Configure store with @reduxjs/toolkit
  const store = configureStore({
    reducer: chatReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: true,
      }),
    devTools: process.env.NODE_ENV !== 'production', // Include dev tools in non-production environments
  });

  return store;
}

function evictAdminSession(adminTimes: AdminAccessTimes): string {
  let minTimeTs = Number.MAX_VALUE;
  let minSessionId: string;
  for (const sessionId of Object.keys(adminTimes)) {
    if (adminTimes[sessionId].lastAccessMs < minTimeTs) {
      minSessionId = sessionId;
      minTimeTs = adminTimes[sessionId].lastAccessMs;
    }
  }

  return minSessionId;
}

function generateKey(connection: ServerConfig, sessionId?: string): string {
  const base = `xappchat.${connection.serverUrl}`;
  if (sessionId) {
    return connection.accountKey
      ? `${base}.${connection.accountKey}.${sessionId}`
      : `${base}.${sessionId}`;
  } else {
    return connection.accountKey ? `${base}.${connection.accountKey}` : base;
  }
}
